import type { TrackerKpi, Week } from 'chery-contracts';
import { isNumericUnit, PILLAR_META, type RangeSummary } from 'chery-kpi-engine';
import { csvDocument, csvNumber, csvText, UNIT_LABELS, VALUE_TYPE_LABELS } from '../../lib/csv';
import type { CellValue } from './tracker-state';

/**
 * CSV export of exactly what the grid shows (plan §3.1, §7): the KPIs on screen (search and pillar filter
 * applied, collapsed pillars included), the weeks of the range, every value as displayed — one decimal, staged
 * edits included, calculated KPIs as computed in the browser — and the Total / Average / Latest / Target columns
 * (the target is the one covering the To week, as the grid shows it). The CSV rules themselves (BOM, CRLF, plain
 * numbers, formula neutralisation) live in `lib/csv.ts`, shared with the Reports export.
 */

const valueCell = (kpi: Pick<TrackerKpi, 'unit'>, value: CellValue): string => {
  if (value === null) {
    return '';
  }

  if (typeof value === 'string' || !isNumericUnit(kpi.unit)) {
    return csvText(String(value));
  }

  return csvNumber(value);
};

export interface TrackerCsvInput {
  kpis: readonly TrackerKpi[];
  weeks: readonly Pick<Week, 'id' | 'weekDate'>[];
  valueAt: (kpiId: string, weekId: string) => CellValue;
  summaryOf: (kpiId: string) => RangeSummary;
  /** The value of the target the grid shows for the KPI, or `null` (an empty cell). */
  targetOf: (kpiId: string) => number | null;
}

export const TRACKER_CSV_FIXED_HEADERS = ['Pillar', 'Code', 'KPI', 'Unit', 'Currency', 'Value type'] as const;
export const TRACKER_CSV_SUMMARY_HEADERS = ['Total', 'Average', 'Latest', 'Target'] as const;

/** Header row: the fixed columns, one column per week (its reporting Sunday, `YYYY-MM-DD`), then the summaries. */
export const buildTrackerCsv = ({ kpis, weeks, valueAt, summaryOf, targetOf }: TrackerCsvInput): string => {
  const header = [...TRACKER_CSV_FIXED_HEADERS, ...weeks.map((week) => week.weekDate), ...TRACKER_CSV_SUMMARY_HEADERS];
  const rows = kpis.map((kpi) => {
    const summary = summaryOf(kpi.id);

    return [
      csvText(PILLAR_META[kpi.pillar].label),
      csvText(kpi.code),
      csvText(kpi.name),
      UNIT_LABELS[kpi.unit],
      kpi.currency ?? '',
      VALUE_TYPE_LABELS[kpi.valueType],
      ...weeks.map((week) => valueCell(kpi, valueAt(kpi.id, week.id))),
      csvNumber(summary.total),
      csvNumber(summary.average),
      csvNumber(summary.latest),
      csvNumber(targetOf(kpi.id)),
    ];
  });

  return csvDocument([header, ...rows]);
};

/** `chery-tracker-2026-01-04_to_2026-10-04.csv`. */
export const trackerCsvFileName = (weeks: readonly Pick<Week, 'weekDate'>[]): string =>
  `chery-tracker-${weeks[0]?.weekDate ?? 'empty'}_to_${weeks[weeks.length - 1]?.weekDate ?? 'empty'}.csv`;
